import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../context/LanguageContext";
import { api, bookUrl } from "../lib/api";
import { Scroll, Search, X } from "lucide-react";
import { Input } from "../components/ui/input";
import BookshelfSection from "../components/library/BookshelfSection";
import "../components/library/bookshelf.css";

export default function Library() {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");

  // Synthetic "Tefilah" (prayer) shelf — the Siddur lives at /siddur
  // rather than under /reader, so it isn't part of the Sefaria-driven
  // `api.books()` payload. We inject it here so the library shows ALL
  // our reading surfaces (Tanakh + Siddur) on physical-looking shelves.
  // Built at render-time so the labels honour the current language.
  const TEFILAH_SECTION = useMemo(() => ({
    key: "tefilah",
    he: t("library_page.tefilah_section_he"),
    en: t("library_page.tefilah_section_en"),
    books: [
      {
        slug: "siddur",
        he: t("library_page.siddur_he"),
        en: t("library_page.siddur_en"),
        chapters: 1,            // not used on the shelf but kept for shape parity
        route: "/siddur",       // BookSpine uses `book.route` when present
      },
    ],
  }), [t]);

  useEffect(() => {
    api.books().then(setData);
  }, []);

  // Hebrew + English fuzzy filter on book title (case-insensitive, ignore nikud)
  const filtered = useMemo(() => {
    if (!data) return null;
    // Append our synthetic Tefilah shelf (Siddur) after Sefaria's
    // Tanakh sections so the library shows every reading surface.
    const sectionsWithTefilah = [...data.sections, TEFILAH_SECTION];
    const q = query.trim().toLowerCase();
    if (!q) return sectionsWithTefilah;
    const stripNikud = (s = "") => s.normalize("NFKD").replace(/[\u0591-\u05C7]/g, "");
    const matches = (book) => {
      const he = stripNikud(book.he || "").toLowerCase();
      const en = (book.en || "").toLowerCase();
      const slug = (book.slug || "").toLowerCase();
      return he.includes(stripNikud(q)) || en.includes(q) || slug.includes(q);
    };
    return sectionsWithTefilah
      .map((s) => ({ ...s, books: s.books.filter(matches) }))
      .filter((s) => s.books.length > 0);
  }, [data, query, TEFILAH_SECTION]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-14" data-testid="library-page">
      <div className="text-center mb-8 sm:mb-14 space-y-3 animate-fade-in-up">
        <span className="font-latin italic text-xs sm:text-sm tracking-widest uppercase text-gold">
          {/* Bilingual eyebrow — same pattern as the Landing hero
              ("Sefer · ספר"). Order flips to put the active locale
              first so the page reads naturally in either mode. */}
          {isEn
            ? `${t("library_page.eyebrow_latin")} · ${t("library_page.eyebrow_he")}`
            : `${t("library_page.eyebrow_latin")} · ${t("library_page.eyebrow_he")}`}
        </span>
        <h1 className="font-heading text-3xl sm:text-5xl md:text-6xl text-foreground">
          {t("library_page.title")}
        </h1>
        <div className="ornamental-divider mx-auto max-w-md">✦ ❖ ✦</div>
        <p className="text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
          {t("library_page.subtitle")}
        </p>
      </div>

      {/* Search bar — sticky on mobile so it's always reachable while scrolling */}
      <div
        className="sticky top-[48px] sm:static z-20 -mx-4 sm:mx-0 mb-6 sm:mb-10 bg-background/95 backdrop-blur-md sm:bg-transparent sm:backdrop-blur-none px-4 sm:px-0 py-3 sm:py-0 border-b border-border/40 sm:border-0"
        data-testid="library-search-bar"
      >
        <div className="relative max-w-md mx-auto">
          {/* Search icon stays on the leading edge for the active
              language — right for HE (RTL), left for EN (LTR). */}
          <Search className={`absolute ${isEn ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none`} strokeWidth={1.5} />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("library_page.search_placeholder")}
            dir={isEn ? "ltr" : "rtl"}
            data-testid="library-search-input"
            className={`${isEn ? "pl-10 pr-10 text-left" : "pe-10 ps-10 text-right"} bg-card border-border/60 focus-visible:ring-gold font-body text-sm sm:text-base h-11`}
          />
          {query && (
            <button
              type="button"
              data-testid="library-search-clear"
              onClick={() => setQuery("")}
              aria-label={t("library_page.search_clear_aria")}
              className={`absolute ${isEn ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground`}
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      {!data && <LibrarySkeleton />}

      {filtered && filtered.length === 0 && (
        <p
          className="text-center text-muted-foreground font-heading text-base sm:text-lg py-10"
          data-testid="library-empty-state"
        >
          {t("library_page.empty_state")}
        </p>
      )}

      {filtered?.map((section, idx) => {
        // All shelves render as wooden bookshelves now (Torah ·
        // Nevi'im · Ketuvim · Tefilah). Same component handles
        // small shelves (1 spine for the Siddur) up to 21 spines
        // (Nevi'im, with smooth horizontal scroll).
        return (
          <div
            key={section.key}
            style={{ animationDelay: `${idx * 80}ms` }}
            className="animate-fade-in-up"
          >
            <BookshelfSection section={section} />
          </div>
        );
      })}
    </div>
  );
}

function LibrarySkeleton() {
  return (
    <div className="space-y-10">
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-4">
          <div className="h-10 shimmer-bg rounded w-48" />
          <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, j) => (
              <div key={j} className="h-24 sm:h-28 shimmer-bg rounded" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
